// frontend/src/pages/Reinsurance/components/CedentManagement/CedentDetail/index.js

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft, faEdit, faExclamationCircle, faSync,
  faBuilding, faUser, faGlobe, faChartLine, 
  faFileContract, faClipboardList, faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { useCedent } from '../../../contexts/CedentContext';
import { reinsuranceService } from '../../../../../services/reinsurance';
import styles from './CedentDetail.module.css';

const CedentDetail = ({ cedentId, onBack, onEdit }) => {
  const { t } = useLanguage();
  const { fetchCedentById, currentCedent, loading, error, clearError } = useCedent();
  const [activeTab, setActiveTab] = useState('overview');
  const [performanceData, setPerformanceData] = useState(null);
  const [loadingPerformance, setLoadingPerformance] = useState(false);
  const [performanceError, setPerformanceError] = useState(null);
  const performanceDataLoaded = useRef(false);
  
  // Fetch cedent details
  const loadCedentDetails = useCallback(async () => {
    if (!cedentId) return;
    
    clearError();
    await fetchCedentById(cedentId);
  }, [cedentId, fetchCedentById, clearError]);
  
  // Fetch cedent performance data
  const loadPerformanceData = useCallback(async () => {
    if (!cedentId) return;
    
    setLoadingPerformance(true);
    setPerformanceError(null);
    
    try {
      const response = await reinsuranceService.getCedentPerformance(cedentId);
      
      if (response.success) {
        setPerformanceData(response.data);
      } else {
        throw new Error(response.message || 'Failed to load performance data');
      }
    } catch (err) {
      console.error('Error fetching cedent performance:', err);
      setPerformanceError(err.message || 'Failed to load performance data. Please try again.');
    } finally {
      setLoadingPerformance(false);
    }
  }, [cedentId]);
  
  // Fetch data on component mount
  useEffect(() => {
    loadCedentDetails();
  }, [loadCedentDetails]);
  
  // Fetch performance data when switching to performance tab
  useEffect(() => {
    if (activeTab === 'performance' && !performanceData && !loadingPerformance && !performanceDataLoaded.current) {
      performanceDataLoaded.current = true;
      loadPerformanceData();
    }
  }, [activeTab, performanceData, loadingPerformance, loadPerformanceData]);
  
  // Format currency
  const formatCurrency = (amount, currency = 'USD') => {
    return reinsuranceService.formatCurrency(amount, currency);
  };
  
  // Format date
  const formatDate = (dateString) => {
    return reinsuranceService.formatDate(dateString);
  };
  
  if (loading) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner}></div>
        <p>{t('loading_cedent_details')}</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className={styles.error}>
        <FontAwesomeIcon icon={faExclamationCircle} className={styles.errorIcon} />
        <p>{error}</p>
        <button className={styles.retryButton} onClick={loadCedentDetails}>
          <FontAwesomeIcon icon={faSync} />
          <span>{t('retry')}</span>
        </button>
      </div>
    );
  }
  
  if (!currentCedent) {
    return (
      <div className={styles.error}>
        <FontAwesomeIcon icon={faExclamationCircle} className={styles.errorIcon} />
        <p>{t('cedent_not_found')}</p>
        <button className={styles.backButton} onClick={onBack}>
          <FontAwesomeIcon icon={faArrowLeft} />
          <span>{t('back_to_cedents')}</span>
        </button>
      </div>
    );
  }
  
  return (
    <div className={styles.cedentDetail}>
      <div className={styles.header}>
        <button className={styles.backButton} onClick={onBack}>
          <FontAwesomeIcon icon={faArrowLeft} />
          <span>{t('back_to_cedents')}</span>
        </button>
        <h1 className={styles.title}>{currentCedent.cedent_name}</h1>
        <button className={styles.editButton} onClick={onEdit}>
          <FontAwesomeIcon icon={faEdit} />
          <span>{t('edit_cedent')}</span>
        </button>
      </div>
      
      <div className={styles.statusBar}>
        <div className={styles.statusItem}>
          <span className={styles.statusLabel}>{t('domicile')}:</span>
          <span className={styles.statusValue}>{currentCedent.cedent_domicile || '-'}</span>
        </div>
        <div className={styles.statusItem}>
          <span className={styles.statusLabel}>{t('type')}:</span>
          <span className={styles.statusValue}>{currentCedent.cedent_type || '-'}</span>
        </div>
        <div className={styles.statusItem}>
          <span className={styles.statusLabel}>{t('am_best_rating')}:</span>
          <span className={styles.statusValue}>{currentCedent.cedent_rating?.am_best || '-'}</span>
        </div>
      </div>
      
      <div className={styles.tabs}>
        <button 
          className={`${styles.tabButton} ${activeTab === 'overview' ? styles.active : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          <FontAwesomeIcon icon={faInfoCircle} />
          <span>{t('overview')}</span>
        </button>
        <button 
          className={`${styles.tabButton} ${activeTab === 'contact' ? styles.active : ''}`}
          onClick={() => setActiveTab('contact')}
        >
          <FontAwesomeIcon icon={faUser} />
          <span>{t('contact')}</span>
        </button>
        <button 
          className={`${styles.tabButton} ${activeTab === 'financial' ? styles.active : ''}`}
          onClick={() => setActiveTab('financial')}
        >
          <FontAwesomeIcon icon={faChartLine} />
          <span>{t('financial')}</span>
        </button>
        <button 
          className={`${styles.tabButton} ${activeTab === 'underwriting' ? styles.active : ''}`}
          onClick={() => setActiveTab('underwriting')}
        >
          <FontAwesomeIcon icon={faGlobe} />
          <span>{t('underwriting')}</span>
        </button>
        <button 
          className={`${styles.tabButton} ${activeTab === 'contracts' ? styles.active : ''}`}
          onClick={() => setActiveTab('contracts')}
        >
          <FontAwesomeIcon icon={faFileContract} />
          <span>{t('contracts')}</span>
        </button>
        <button 
          className={`${styles.tabButton} ${activeTab === 'performance' ? styles.active : ''}`}
          onClick={() => setActiveTab('performance')}
        >
          <FontAwesomeIcon icon={faClipboardList} />
          <span>{t('performance')}</span>
        </button>
      </div>
      
      <div className={styles.tabContent}>
        {activeTab === 'overview' && (
          <div className={styles.overviewTab}>
            <div className={styles.overviewGrid}>
              <div className={styles.overviewCard}>
                <h3 className={styles.cardTitle}>
                  <FontAwesomeIcon icon={faBuilding} />
                  <span>{t('cedent_details')}</span>
                </h3>
                <div className={styles.cardContent}>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>{t('cedent_name')}</span>
                    <span className={styles.detailValue}>{currentCedent.cedent_name}</span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>{t('cedent_domicile')}</span>
                    <span className={styles.detailValue}>{currentCedent.cedent_domicile}</span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>{t('cedent_type')}</span>
                    <span className={styles.detailValue}>{currentCedent.cedent_type}</span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>{t('relationship_since')}</span>
                    <span className={styles.detailValue}>
                      {currentCedent.relationship_information?.relationship_since 
                        ? formatDate(currentCedent.relationship_information.relationship_since) 
                        : '-'}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className={styles.overviewCard}>
                <h3 className={styles.cardTitle}>
                  <FontAwesomeIcon icon={faChartLine} />
                  <span>{t('ratings')}</span>
                </h3>
                <div className={styles.cardContent}>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>{t('am_best')}</span>
                    <span className={styles.detailValue}>{currentCedent.cedent_rating?.am_best || '-'}</span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>{t('sp')}</span>
                    <span className={styles.detailValue}>{currentCedent.cedent_rating?.sp || '-'}</span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>{t('moodys')}</span>
                    <span className={styles.detailValue}>{currentCedent.cedent_rating?.moodys || '-'}</span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>{t('fitch')}</span>
                    <span className={styles.detailValue}>{currentCedent.cedent_rating?.fitch || '-'}</span>
                  </div>
                </div>
              </div>
              
              <div className={styles.overviewCard}>
                <h3 className={styles.cardTitle}>
                  <FontAwesomeIcon icon={faUser} />
                  <span>{t('primary_contact')}</span>
                </h3>
                <div className={styles.cardContent}>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>{t('name')}</span>
                    <span className={styles.detailValue}>{currentCedent.contact_information?.primary_contact?.name || '-'}</span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>{t('title')}</span>
                    <span className={styles.detailValue}>{currentCedent.contact_information?.primary_contact?.title || '-'}</span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>{t('email')}</span>
                    <span className={styles.detailValue}>{currentCedent.contact_information?.primary_contact?.email || '-'}</span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>{t('phone')}</span>
                    <span className={styles.detailValue}>{currentCedent.contact_information?.primary_contact?.phone || '-'}</span>
                  </div>
                </div>
              </div>
              
              <div className={styles.overviewCard}>
                <h3 className={styles.cardTitle}>
                  <FontAwesomeIcon icon={faGlobe} />
                  <span>{t('underwriting_focus')}</span>
                </h3>
                <div className={styles.cardContent}>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>{t('lines_of_business')}</span>
                    <span className={styles.detailValue}>
                      {currentCedent.underwriting_information?.lines_of_business?.length > 0 
                        ? currentCedent.underwriting_information.lines_of_business.join(', ') 
                        : '-'}
                    </span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>{t('territories')}</span>
                    <span className={styles.detailValue}>
                      {currentCedent.underwriting_information?.territories?.length > 0 
                        ? currentCedent.underwriting_information.territories.join(', ') 
                        : '-'}
                    </span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>{t('risk_appetite')}</span>
                    <span className={styles.detailValue}>{currentCedent.underwriting_information?.risk_appetite || '-'}</span>
                  </div>
                </div>
              </div>
            </div>
            
            {currentCedent.relationship_information?.notes && (
              <div className={styles.notesSection}>
                <h3 className={styles.sectionTitle}>{t('relationship_notes')}</h3>
                <div className={styles.notesContent}>
                  <p>{currentCedent.relationship_information.notes}</p>
                </div>
              </div>
            )}
          </div>
        )}
        
        {activeTab === 'contact' && (
          <div className={styles.contactTab}>
            <div className={styles.sectionCard}>
              <h3 className={styles.sectionTitle}>{t('primary_contact')}</h3>
              <div className={styles.sectionContent}>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>{t('name')}</span>
                  <span className={styles.detailValue}>{currentCedent.contact_information?.primary_contact?.name || '-'}</span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>{t('title')}</span>
                  <span className={styles.detailValue}>{currentCedent.contact_information?.primary_contact?.title || '-'}</span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>{t('email')}</span>
                  <span className={styles.detailValue}>
                    {currentCedent.contact_information?.primary_contact?.email ? (
                      <a href={`mailto:${currentCedent.contact_information.primary_contact.email}`}>
                        {currentCedent.contact_information.primary_contact.email}
                      </a>
                    ) : '-'}
                  </span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>{t('phone')}</span>
                  <span className={styles.detailValue}>
                    {currentCedent.contact_information?.primary_contact?.phone ? (
                      <a href={`tel:${currentCedent.contact_information.primary_contact.phone}`}>
                        {currentCedent.contact_information.primary_contact.phone}
                      </a>
                    ) : '-'}
                  </span>
                </div>
              </div>
            </div>
            
            {currentCedent.contact_information?.secondary_contact?.name && (
              <div className={styles.sectionCard}>
                <h3 className={styles.sectionTitle}>{t('secondary_contact')}</h3>
                <div className={styles.sectionContent}>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>{t('name')}</span>
                    <span className={styles.detailValue}>{currentCedent.contact_information.secondary_contact.name}</span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>{t('title')}</span>
                    <span className={styles.detailValue}>{currentCedent.contact_information.secondary_contact.title || '-'}</span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>{t('email')}</span>
                    <span className={styles.detailValue}>
                      {currentCedent.contact_information.secondary_contact.email ? (
                        <a href={`mailto:${currentCedent.contact_information.secondary_contact.email}`}>
                          {currentCedent.contact_information.secondary_contact.email}
                        </a>
                      ) : '-'}
                    </span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>{t('phone')}</span>
                    <span className={styles.detailValue}>
                      {currentCedent.contact_information.secondary_contact.phone ? (
                        <a href={`tel:${currentCedent.contact_information.secondary_contact.phone}`}>
                          {currentCedent.contact_information.secondary_contact.phone}
                        </a>
                      ) : '-'}
                    </span>
                  </div>
                </div>
              </div>
            )}
            
            <div className={styles.sectionCard}>
              <h3 className={styles.sectionTitle}>{t('address')}</h3>
              <div className={styles.sectionContent}>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>{t('street')}</span>
                  <span className={styles.detailValue}>{currentCedent.contact_information?.address?.street || '-'}</span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>{t('city')}</span>
                  <span className={styles.detailValue}>{currentCedent.contact_information?.address?.city || '-'}</span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>{t('state_province')}</span>
                  <span className={styles.detailValue}>{currentCedent.contact_information?.address?.state || '-'}</span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>{t('postal_code')}</span>
                  <span className={styles.detailValue}>{currentCedent.contact_information?.address?.postal_code || '-'}</span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>{t('country')}</span>
                  <span className={styles.detailValue}>{currentCedent.contact_information?.address?.country || '-'}</span>
                </div>
              </div>
            </div>
            
            {currentCedent.relationship_information?.broker_relationships?.length > 0 && (
              <div className={styles.sectionCard}>
                <h3 className={styles.sectionTitle}>{t('broker_relationships')}</h3>
                <div className={styles.tableContainer}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>{t('broker_name')}</th>
                        <th>{t('broker_contact')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentCedent.relationship_information.broker_relationships.map((broker, index) => (
                        <tr key={index}>
                          <td>{broker.broker_name}</td>
                          <td>{broker.broker_contact}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
        
        {activeTab === 'financial' && (
          <div className={styles.financialTab}>
            <div className={styles.sectionCard}>
              <h3 className={styles.sectionTitle}>{t('financial_information')}</h3>
              <div className={styles.sectionContent}>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>{t('currency')}</span>
                  <span className={styles.detailValue}>{currentCedent.financial_information?.currency || 'USD'}</span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>{t('annual_premium_volume')}</span>
                  <span className={styles.detailValue}>
                    {currentCedent.financial_information?.annual_premium_volume 
                      ? formatCurrency(
                          currentCedent.financial_information.annual_premium_volume, 
                          currentCedent.financial_information.currency
                        ) 
                      : '-'}
                  </span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>{t('total_assets')}</span>
                  <span className={styles.detailValue}>
                    {currentCedent.financial_information?.total_assets 
                      ? formatCurrency(
                          currentCedent.financial_information.total_assets, 
                          currentCedent.financial_information.currency
                        ) 
                      : '-'}
                  </span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>{t('am_best_financial_size_category')}</span>
                  <span className={styles.detailValue}>{currentCedent.financial_information?.am_best_financial_size_category || '-'}</span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>{t('solvency_ratio')}</span>
                  <span className={styles.detailValue}>
                    {currentCedent.financial_information?.solvency_ratio 
                      ? `${currentCedent.financial_information.solvency_ratio}%` 
                      : '-'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {activeTab === 'underwriting' && (
          <div className={styles.underwritingTab}>
            <div className={styles.sectionCard}>
              <h3 className={styles.sectionTitle}>{t('underwriting_information')}</h3>
              <div className={styles.sectionContent}>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>{t('lines_of_business')}</span>
                  <span className={styles.detailValue}>
                    {currentCedent.underwriting_information?.lines_of_business?.length > 0 
                      ? currentCedent.underwriting_information.lines_of_business.join(', ') 
                      : '-'}
                  </span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>{t('territories')}</span>
                  <span className={styles.detailValue}>
                    {currentCedent.underwriting_information?.territories?.length > 0 
                      ? currentCedent.underwriting_information.territories.join(', ') 
                      : '-'}
                  </span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>{t('underwriting_approach')}</span>
                  <span className={styles.detailValue}>{currentCedent.underwriting_information?.underwriting_approach || '-'}</span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>{t('risk_appetite')}</span>
                  <span className={styles.detailValue}>{currentCedent.underwriting_information?.risk_appetite || '-'}</span>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {activeTab === 'contracts' && (
          <div className={styles.contractsTab}>
            <div className={styles.emptyState}>
              <p>{t('contracts_tab_coming_soon')}</p>
            </div>
          </div>
        )}
        
        {activeTab === 'performance' && (
          <div className={styles.performanceTab}>
            {loadingPerformance ? (
              <div className={styles.loading}>
                <div className={styles.spinner}></div>
                <p>{t('loading_performance_data')}</p>
              </div>
            ) : performanceError ? (
              <div className={styles.error}>
                <FontAwesomeIcon icon={faExclamationCircle} className={styles.errorIcon} />
                <p>{performanceError}</p>
                <button className={styles.retryButton} onClick={loadPerformanceData}>
                  <FontAwesomeIcon icon={faSync} />
                  <span>{t('retry')}</span>
                </button>
              </div>
            ) : performanceData ? (
              <>
                <div className={styles.overviewGrid}>
                  <div className={styles.overviewCard}>
                    <h3 className={styles.cardTitle}>
                      <FontAwesomeIcon icon={faFileContract} />
                      <span>{t('contract_summary')}</span>
                    </h3>
                    <div className={styles.cardContent}>
                      <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>{t('total_contracts')}</span>
                        <span className={styles.detailValue}>{performanceData.total_contracts}</span>
                      </div>
                      <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>{t('active_contracts')}</span>
                        <span className={styles.detailValue}>{performanceData.active_contracts}</span>
                      </div>
                      <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>{t('total_premium')}</span>
                        <span className={styles.detailValue}>{formatCurrency(performanceData.total_premium)}</span>
                      </div>
                      <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>{t('total_limit')}</span>
                        <span className={styles.detailValue}>{formatCurrency(performanceData.total_limit)}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className={styles.overviewCard}>
                    <h3 className={styles.cardTitle}>
                      <FontAwesomeIcon icon={faClipboardList} />
                      <span>{t('claims_summary')}</span>
                    </h3>
                    <div className={styles.cardContent}>
                      <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>{t('total_claims')}</span>
                        <span className={styles.detailValue}>{performanceData.claims.total}</span>
                      </div>
                      <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>{t('open_claims')}</span>
                        <span className={styles.detailValue}>{performanceData.claims.open}</span>
                      </div>
                      <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>{t('total_incurred')}</span>
                        <span className={styles.detailValue}>{formatCurrency(performanceData.claims.total_incurred)}</span>
                      </div>
                      <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>{t('total_paid')}</span>
                        <span className={styles.detailValue}>{formatCurrency(performanceData.claims.total_paid)}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className={styles.overviewCard}>
                    <h3 className={styles.cardTitle}>
                      <FontAwesomeIcon icon={faChartLine} />
                      <span>{t('performance_metrics')}</span>
                    </h3>
                    <div className={styles.cardContent}>
                      <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>{t('loss_ratio')}</span>
                        <span className={styles.detailValue}>{`${performanceData.loss_ratio}%`}</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                {performanceData.performance_by_year?.length > 0 && (
                  <div className={styles.sectionCard}>
                    <h3 className={styles.sectionTitle}>{t('performance_by_year')}</h3>
                    <div className={styles.tableContainer}>
                      <table className={styles.table}>
                        <thead>
                          <tr>
                            <th>{t('year')}</th>
                            <th>{t('contracts')}</th>
                            <th>{t('premium')}</th>
                            <th>{t('losses')}</th>
                            <th>{t('loss_ratio')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {performanceData.performance_by_year.map((yearData, index) => (
                            <tr key={index}>
                              <td>{yearData.year}</td>
                              <td>{yearData.contracts}</td>
                              <td>{formatCurrency(yearData.premium)}</td>
                              <td>{formatCurrency(yearData.losses)}</td>
                              <td>{`${yearData.loss_ratio}%`}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className={styles.emptyState}>
                <p>{t('no_performance_data_available')}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CedentDetail;